import { Box, Divider, Flex, Text } from '@chakra-ui/react';
import React from 'react';

import { DbTable } from '@finley-technologies/finley-types';

import { DisplayNode } from '../../hooks/instanceGraphContext';
import { COLORS, SIZES } from './consts';
import { NodeStatus, NodeStatusIndicator } from './NodeStatusIndicator';
import { BsCardChecklist } from 'react-icons/bs';
import {
  FaTape,
  FaDollarSign,
  FaClipboardList,
  FaFileAlt,
} from 'react-icons/fa';
import { GiTakeMyMoney, GiCash, GiMoneyStack } from 'react-icons/gi';

// Define the mapping of node types to their corresponding icons, colors, and texts
const NODE_TYPES = {
  tapeBuilder: { icon: FaTape, color: '#FF6F3A', text: 'Tape Builder' },
  loanTape: { icon: FaDollarSign, color: '#FF9C21', text: 'Loan Tape' },
  cashBalance: { icon: GiCash, color: '#3E7E3E', text: 'Cash Balance' },
  borrowingBaseReport: {
    icon: FaFileAlt,
    color: '#2E2E9D',
    text: 'Borrowing Base Report',
  },
  fundingRequest: {
    icon: GiTakeMyMoney,
    color: '#FF3B8A',
    text: 'Funding Request',
  },
  generatedLoanTape: {
    icon: GiMoneyStack,
    color: '#005ED1',
    text: 'Generated Loan Tape',
  },
  receivableResponseTape: {
    icon: BsCardChecklist,
    color: '#7E3E7E',
    text: 'Receivable Response Tape',
  },
  assignment: { icon: FaClipboardList, color: '#4CB7CC', text: 'Assignment' },
  other: { icon: FaFileAlt, color: '#2E2E9D', text: 'Other' },
  backupServicing: {
    icon: FaFileAlt,
    color: '#2E2E9D',
    text: 'Backup Servicing',
  },
};
type NodeType = keyof typeof NODE_TYPES;

const artifactTypeToNodeType: { [key in DbTable.ArtifactType]: NodeType } = {
  rawZipTape: 'other',
  rawLoanTape: 'loanTape',
  rawCashBalanceFeed: 'cashBalance',
  rawRepaymentTape: 'other',
  rawSupplementalTape: 'other',
  configTape: 'other',
  rawReceivableDocumentation: 'other',
  intermediateTape: 'tapeBuilder',
  internalRepaymentTape: 'other',
  cashResponseTape: 'cashBalance',
  cashBalancesFeedContext: 'cashBalance',
  masterLoanTape: 'loanTape',
  manualBankAccountReport: 'cashBalance',
  assignedLoanTape: 'loanTape',
  receivableResponseTapePreview: 'receivableResponseTape',
  receivableResponseTape: 'receivableResponseTape',
  borrowingBaseReport: 'borrowingBaseReport',
  receivableInvoice: 'other',
  fundingVerificationReport: 'loanTape',
  fundingApprovalReport: 'loanTape',
  fundingRequest: 'fundingRequest',
  receivableDocumentationVerificationReport: 'other',
  forwardedTape: 'other',
  valleyBankIngest: 'other',
  valleyBankSlsIngest: 'other',
  valleyBankSlsCommitment: 'other',
  orgProvidedInput: 'other',
  orgProvidedInputs: 'other',
  generatedCovenantLoanTape: 'other',
  generatedLoanTape: 'generatedLoanTape',
  generatedCashBalanceFeed: 'cashBalance',
  generatedRepaymentTape: 'other',
  assignedRepaymentTape: 'other',
  generatedSupplementalTape: 'other',
  generatedFinancialData: 'other',
  userInputNeeded: 'other',
  uploadToDatalake: 'other',
  waitForDataLakeTable: 'other',
  dataLakeTape: 'other',
  limitedPartnersTape: 'other',
  finStatement: 'other',
  directInsertBorrowingBaseReport: 'borrowingBaseReport',
  directInsertPortfolioCovenant: 'other',
  directInsertPortfolioCovenants: 'other',
  directInsertFinancialCovenants: 'other',
  reconciledBusTape: 'backupServicing',
  busCertificate: 'backupServicing',
} as const;

// Node component that renders header with icon and text based on the node type
// also renders a comment icon with a red dot to indicate that there are comments
export const NodeHeader = ({
  node,
  extendedStatus,
}: {
  node: DisplayNode;
  extendedStatus: NodeStatus;
}) => {
  const { artifact, instance } = node;
  const artifactType = artifact.type;

  const nodeType = artifactTypeToNodeType[artifactType];
  const nodeIconAndText = NODE_TYPES[nodeType];

  // for tapebuilder nodes use operation as secibdart text, others use artifactSlug
  const secondaryText =
    nodeType === 'tapeBuilder'
      ? artifact.configuration?.operation
      : artifact.externalDisplayName;

  const getInstanceTime = () => {
    if (!instance) {
      return null;
    }
    if ('endTimeMs' in instance.statusDetails) {
      const { startTimeMs, endTimeMs } = instance.statusDetails;
      const duration = endTimeMs - startTimeMs;

      // return duration in h:m:s format
      const hours = Math.floor(duration / 3600000);
      const minutes = Math.floor((duration % 3600000) / 60000);
      const seconds = Math.floor((duration % 60000) / 1000);
      if (hours !== 0) {
        return `${hours}:${minutes}:${seconds}`;
      }

      return `${minutes}m:${seconds}s`;
    }

    return null;
  };

  const instanceTime = getInstanceTime();

  if (!nodeIconAndText) {
    return null; // or render a default/fallback component
  }

  return (
    <>
      {extendedStatus !== 'none' && (
        <>
          <Box p={SIZES.padding}>
            <NodeStatusIndicator status={extendedStatus} />
          </Box>
          <Divider borderColor={COLORS.divider} />
        </>
      )}
      <Flex w="100%" p={SIZES.padding} gap={SIZES.gapSmall} alignItems="center">
        <Text textAlign="center" color={nodeIconAndText.color} fontSize="12px">
          <nodeIconAndText.icon />
        </Text>
        <Text
          color={nodeIconAndText.color}
          fontSize={SIZES.textSmall}
          fontWeight="500"
          lineHeight="20px"
        >
          {nodeIconAndText.text}
        </Text>
      </Flex>
      <Divider borderColor={COLORS.divider} />
      <Flex w="100%" p={SIZES.padding} gap={SIZES.gapSmall} alignItems="center">
        <Text
          flex="1"
          color={COLORS.secondary}
          fontSize={SIZES.textMedium}
          fontWeight="600"
          noOfLines={1}
          wordBreak="break-all"
        >
          {secondaryText}
        </Text>
        {instanceTime && (
          <Text color={COLORS.secondaryText} fontSize={SIZES.textSmall}>
            {instanceTime}
          </Text>
        )}
      </Flex>
    </>
  );
};
